import moment from 'moment'

import { calculateEndDate, isCustomItem, isFlightSearchBookmark, isReturnFlightSearchBookmark } from '.'
import {
  byItineraryRank,
  bySummaryRank,
} from './sortable'

import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { CustomItem, ExperienceItem, TripItem } from 'tripPlanner/types/tripItem'
import { itemIsInItinerary } from 'tripPlanner/components/Itinerary/utils'
import invariant from 'tiny-invariant'

export const NO_DATES = 'NO_DATES'
export const SUGGESTED = 'SUGGESTED'

export type ItinerarySection = moment.Moment | typeof NO_DATES | typeof SUGGESTED

export function itinerarySectionToString(section: ItinerarySection | string) {
  return typeof section === 'string' ? section : section.format(ISO_DATE_FORMAT)
}

export function itinerarySectionElementId(section: ItinerarySection | string) {
  return `itinerary-section-${itinerarySectionToString(section)}`
}

export function getRange(startDate: moment.Moment, endDate: moment.Moment) {
  const fromDate = moment(startDate)
  const toDate = moment(endDate)
  const diff = toDate.diff(fromDate, 'day')
  const range: Array<moment.Moment> = []
  for (let i = 0; i <= diff; i++) {
    range.push(moment(startDate).add(i, 'day'))
  }
  return range
}

function showInSuggestedSection(item: TripItem) {
  if (isFlightSearchBookmark(item)) {
    return false
  }
  return item.isSuggested
}

export function getStartItemsBySection(
  items: Array<TripItem>,
  startDate?: moment.Moment,
  endDate?: moment.Moment,
): Array<{
  date: ItinerarySection
  startItems: Array<TripItem>
}> {
  if (!startDate || !endDate) {
    return [{
      date: NO_DATES as 'NO_DATES',
      startItems: items.sort(bySummaryRank),
    }]
  }

  const presentationsBySection: {
    [section: string]: {
      startItems: Array<TripItem>
    }
  } = {
    [NO_DATES]: {
      startItems: [],
    },
    [SUGGESTED]: {
      startItems: [],
    },
  }
  const range = getRange(startDate, endDate)

  range.forEach((date) => {
    presentationsBySection[date.format(ISO_DATE_FORMAT)] = {
      startItems: [],
    }
  })

  items.forEach((item) => {
    if (showInSuggestedSection(item)) {
      presentationsBySection[SUGGESTED].startItems.push(item)
    } else if (itemIsInItinerary(item, { startDate, endDate })) {
      const isoDate = item.startDate!.format(ISO_DATE_FORMAT)
      presentationsBySection[isoDate].startItems.push(item)
    } else {
      presentationsBySection[NO_DATES].startItems.push(item)
    }
  })

  const includeSuggestedSection = presentationsBySection[SUGGESTED].startItems.length > 0
  const includeNonDateSection = presentationsBySection[NO_DATES].startItems.length > 0

  return [
    ...(includeSuggestedSection ? [{
      date: SUGGESTED as 'SUGGESTED',
      startItems: presentationsBySection[SUGGESTED].startItems.sort(bySummaryRank),
    }] : []),
    ...(includeNonDateSection ? [{
      date: NO_DATES as 'NO_DATES',
      startItems: presentationsBySection[NO_DATES].startItems.sort(bySummaryRank),
    }] : []),
    ...range.map((date) => ({
      date,
      startItems: presentationsBySection[
        date.format(ISO_DATE_FORMAT)
      ].startItems.sort(byItineraryRank),
    })),
  ]
}

const typesWithIntermediaryPresentations = new Set<TripItem['type']>([
  'FLIGHT',
  'ACCOMMODATION',
  'TRAIN',
  'FERRY',
  'BUS',
  'OTHER_TRANSPORT',
])

const typesWithEndPresentations = new Set<TripItem['type']>([
  'FLIGHT',
  'CAR_RENTAL',
  'ACCOMMODATION',
  'TRAIN',
  'FERRY',
  'BUS',
  'OTHER_TRANSPORT',
])

function hasActualDate<T extends { date: ItinerarySection }>(day: T): day is T & { date: moment.Moment } {
  return moment.isMoment(day.date)
}

export function addIntermediateAndEndItems(
  days: Array<{
    date: ItinerarySection
    startItems: Array<TripItem>
  }>,
  items: Array<TripItem>,
): Array<{
  date: ItinerarySection
  startItems: Array<TripItem>
  intermediaryItems: Array<TripItem>
  endItems: Array<TripItem>
}> {
  const itemsByDate: {
    [date: string]: {
      startItems: Array<TripItem>
      intermediaryItems: Array<TripItem>
      endItems: Array<TripItem>
    }
  } = {}

  const startDate = days.find(hasActualDate)!.date
  const endDate = days[days.length - 1].date
  invariant(moment.isMoment(endDate))

  days.forEach((day) => {
    const dayStr = itinerarySectionToString(day.date)
    itemsByDate[dayStr] = {
      startItems: day.startItems,
      intermediaryItems: [],
      endItems: [],
    }
  })

  // Due to the way the drag and drop is done, the start items may have locally-updated start dates
  // that are not reflected in the items array. We need to merge the start items with the items array
  // to ensure that we have the current start dates.
  const startItems = days.flatMap((day) => day.startItems)
  const startItemIds = new Set(startItems.map((item) => item.id))
  const mergedItems = [
    ...startItems,
    ...items.filter((item) => !startItemIds.has(item.id)),
  ]

  mergedItems.forEach((item) => {
    const itemStartDate = item.startDate
    const itemEndDate = 'endDate' in item ? item.endDate : undefined

    if (!itemStartDate || !itemEndDate?.isAfter(itemStartDate, 'day')) {
      return
    }

    const shouldIncludeEndItem =
      typesWithEndPresentations.has(item.type) &&
      itemEndDate.isBetween(startDate, endDate, 'day', '[]') &&
      !isReturnFlightSearchBookmark(item)

    if (shouldIncludeEndItem) {
      itemsByDate[itemEndDate.format(ISO_DATE_FORMAT)].endItems.push(item)
    }

    const shouldIncludeIntermediaryItems =
      typesWithIntermediaryPresentations.has(item.type) &&
      !isReturnFlightSearchBookmark(item)

    if (shouldIncludeIntermediaryItems) {
      const firstIntermediateDay = moment.max(
        itemStartDate.clone().add(1, 'day'),
        startDate.clone(),
      )
      const lastIntermediateDay = moment.min(
        itemEndDate.clone().subtract(1, 'day'),
        endDate,
      )
      for (
        let date = firstIntermediateDay;
        !date.isAfter(lastIntermediateDay);
        date.add(1, 'day')
      ) {
        itemsByDate[
          date.format(ISO_DATE_FORMAT)
        ].intermediaryItems.push(item)
      }
    }
  })

  return days.map((day) => {
    const date = day.date
    const dateString = itinerarySectionToString(date)
    return {
      date,
      startItems: itemsByDate[dateString].startItems,
      // For end or intermediary tiles, there won't often be more than one on the same day,
      // so it doesn't really matter how we sort these. Using the itinerary rank doesn't
      // really make sense because they might not be on the same day.
      // Using the summary rank is as good a way as any.
      intermediaryItems: itemsByDate[dateString].intermediaryItems.sort(
        bySummaryRank,
      ),
      endItems: itemsByDate[dateString].endItems.sort(
        bySummaryRank,
      ),
    }
  })
}

export function updateItemStartDate(
  item: TripItem,
  newStartDate: moment.Moment | undefined,
): TripItem {
  if (newStartDate) {
    if (newStartDate.isSame(item.startDate)) {
      return item
    } else if (item.startDate && 'endDate' in item && item.endDate) {
      const duration = item.endDate.diff(item.startDate, 'day')
      const newEndDate = calculateEndDate(newStartDate, duration)
      return { ...item, startDate: newStartDate, endDate: newEndDate }
    } else {
      return { ...item, startDate: newStartDate }
    }
  } else {
    if ('endDate' in item) {
      return { ...item, startDate: undefined, endDate: undefined }
    } else {
      return { ...item, startDate: undefined }
    }
  }
}

export function canSetItemDatesFromOnlyStartDate(
  item: TripItem,
): boolean {
  return isCustomItem(item) || !!item.startDate || item.type === 'EXPERIENCE'
}

export function canSetItemStartDateFromNoDates(
  item: TripItem,
): item is CustomItem | ExperienceItem {
  return isCustomItem(item) || item.type === 'EXPERIENCE'
}
